import { useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import { AppState } from "../../state/AppState";
import _ from "lodash";
import { SsoService } from "../../models/SsoService";
import Tenants from "../tenants/Tenants";
import Users from "../users/Users";
import { useUsers } from "../../contexts/users";
import AppSettings from "../app-settings/AppSettings";
import CoverageInquiry from "../coverage-inquiry/CoverageInquiry";
import ClaimInquiry from "../claim-inquiry/ClaimInquiry";
import MyInsurance from "../my-insurance/MyInsurance";
import { UserTypes } from "../../models/UserTypes";
import { useEffect, useMemo } from "react";
import MemberClaimInquiry from "../my-claims/MemberClaimInquiry";
import AdminUsers from "../super-admin/AdminUsers";

export default function App() {
    const ssoService = useMemo(() => new SsoService(), []);
    const { decodedAccessToken, isAuthenticated } = useSelector((state: AppState) => state.authenticationState);
    const {
        userType,
        userState: { loggedInUser },
    } = useUsers();

    const isAuthenticatedUser = isAuthenticated && decodedAccessToken?.tenant_name;

    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    window.addEventListener("resize", () => {
        vh = window.innerHeight * 0.01;
        debouncedSetViewportHeight(vh);
    });

    const debouncedSetViewportHeight = _.debounce((vh: number) => {
        document.documentElement.style.setProperty("--vh", `${vh}px`);
    }, 100);

    useEffect(() => {
        if (!isAuthenticatedUser) {
            ssoService.signIn();
        }
    }, [isAuthenticatedUser, ssoService]);

    const isGlobalAdmin = userType === UserTypes.GlobalAdmin;
    const isSuperAdmin = userType === UserTypes.SuperAdmin;

    if (isAuthenticatedUser)
        return (
            <div className="main-content">
                {/* As new routes are added, be sure to update components/header/PageTitle */}
                <Switch>
                    {isGlobalAdmin && (
                        <Route path="/app-settings">
                            <AppSettings />
                        </Route>
                    )}
                    {isGlobalAdmin && (
                        <Route path="/tenants">
                            <Tenants />
                        </Route>
                    )}
                    {isGlobalAdmin && (
                        <Route path="/users">
                            <Users />
                        </Route>
                    )}
                    {loggedInUser && userType === UserTypes.Provider && (
                        <Route path="/coverage-inquiry/coverage-summary">
                            <MyInsurance />
                        </Route>
                    )}

                    {loggedInUser && isSuperAdmin && (
                        <Route path="/admin-users">
                            <AdminUsers />
                        </Route>
                    )}

                    <Route path="/coverage-inquiry">
                        <CoverageInquiry />
                    </Route>

                    <Route path="/claim-inquiry">
                        <ClaimInquiry />
                    </Route>
                    {loggedInUser && userType === UserTypes.Member && (
                        <Route path="/my-insurance">
                            <MyInsurance />
                        </Route>
                    )}

                    {loggedInUser && userType === UserTypes.Member && (
                        <Route path="/my-claims">
                            <MemberClaimInquiry />
                        </Route>
                    )}

                    {loggedInUser && isSuperAdmin && <Redirect from="/" to="/admin-users" />}
                    {loggedInUser && userType === UserTypes.Member && <Redirect from="/" to="/my-insurance" />}
                    {loggedInUser && userType !== UserTypes.Member && <Redirect from="/" to="/coverage-inquiry" />}
                </Switch>
            </div>
        );

    return null;
}
